import { apiClient } from './../client';
import type { IApiResponse, IPaginatedResponse } from './../types';

/**
 * Media asset as returned by `POST /media-assets` and the `data[]` entries of
 * `GET /media-assets` (mirrors backend `MediaAssetResponseDto`, Task 7).
 */
/** Mirrors backend `MediaAssetKind`. */
export type MediaAssetKind = 'IMAGE' | 'VCARD' | 'VIDEO' | 'AUDIO' | 'DOCUMENT';

/**
 * Which server-side rule set an upload is validated against — the email
 * builder's image library (5 MB, four image types) or MMS (600 KB, thirteen).
 * Omitted means `IMAGE_LIBRARY`.
 */
export type MediaProfile = 'IMAGE_LIBRARY' | 'MMS';

/**
 * THREE URLS, AND THEY ARE NOT INTERCHANGEABLE.
 *
 * The media bucket is private, so `publicUrl` is NOT fetchable by a browser
 * unless a CDN fronts it — it is the durable identifier you PERSIST.
 * `displayUrl` is a short-lived presigned GET: render it, never store it (it
 * 403s about an hour later). `embedUrl` is durable AND fetchable — use it for
 * anything frozen into sent content, i.e. an `<img>` in an email.
 *
 *   store  -> publicUrl
 *   render -> displayUrl
 *   embed  -> embedUrl
 */
export interface IMediaAsset {
    readonly id: string;
    readonly kind: MediaAssetKind;
    /** Durable identifier. Safe to persist; not necessarily loadable. */
    readonly publicUrl: string;
    /** Presigned GET for `<img src>`. NEVER persist. */
    readonly displayUrl: string;
    /** Durable + loadable. For email HTML and other frozen content. */
    readonly embedUrl: string;
    readonly filename: string;
    readonly width: number | null;
    readonly height: number | null;
    readonly sizeBytes: number;
    readonly contentType: string;
    readonly createdAt: string;
}

export interface IMediaAssetQuery {
    readonly search?: string;
    readonly page?: number;
    readonly limit?: number;
    readonly profile?: MediaProfile;
}

export interface IPresignMediaAssetInput {
    readonly filename: string;
    readonly contentType: string;
    readonly sizeBytes: number;
    readonly profile?: MediaProfile;
}

export interface IPresignMediaAssetResult {
    readonly uploadUrl: string;
    readonly storageKey: string;
}

export interface IConfirmMediaAssetInput {
    readonly storageKey: string;
    readonly filename: string;
    readonly width?: number;
    readonly height?: number;
}

const BASE = '/media-assets';

export const mediaLibraryApi = {
    presign: (data: IPresignMediaAssetInput) =>
        apiClient
            .post<IApiResponse<IPresignMediaAssetResult>>(`${BASE}/presign`, data)
            .then((r) => r.data.data),

    confirm: (data: IConfirmMediaAssetInput) =>
        apiClient.post<IApiResponse<IMediaAsset>>(BASE, data).then((r) => r.data.data),

    list: (params?: IMediaAssetQuery) =>
        apiClient
            .get<
                IApiResponse<readonly IMediaAsset[]> & { meta: IPaginatedResponse<IMediaAsset> }
            >(BASE, { params })
            .then((r) => ({
                data: r.data.data,
                page: r.data.meta.page,
                limit: r.data.meta.limit,
                total: r.data.meta.total,
                totalPages: r.data.meta.totalPages,
            })),

    remove: (id: string) =>
        apiClient.delete<IApiResponse<null>>(`${BASE}/${id}`).then((r) => r.data.data),
};
